import React, { useState, useEffect, useRef } from 'react';
import { Spin, message, Popover } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment-timezone';
import ConfirmationModal from 'src/components/common/ConfirmationModal';
import UserContext from '../../../../services/UserContex';
import { priceCollectedTime } from '../../services/alertConfiguration.service';
import { getSourceOwner } from '../../api/productAssociation.api';

import styles from './RunAlertConfirmationModal.module.less';

type RunAlertConfirmationModalProps = {
  visible: boolean;
  alertName: string;
  placeholder?: string;
  onClose: () => void;
  closeModalWithUpdate: () => void;
  onConfirm: () => Promise<unknown>;
};

const contentPopover = (
  <div className={styles.contentPopover}>
    <p>Existing results for an alert for today's date will be overwritten.</p>
    <p>Executing a run for an Inactive Segment will provide no results.</p>
  </div>
);

export const RunAlertConfirmationModal: React.FC<RunAlertConfirmationModalProps> = ({
  visible,
  alertName,
  onClose,
  onConfirm,
  closeModalWithUpdate,
}) => {
  const [time, setTime] = useState<string>('');
  const [isRun, setIsRun] = useState<boolean>(false);
  const refIsMounted = useRef(true);

  // TODO: this is a temporary approach until `sourceOwnerId` included into `qm/alert/retrieve` API
  const getDefaultSourceOwnerId = async (): Promise<string> => {
    const { sourceOwner } = await getSourceOwner();
    const defaultOwner = sourceOwner.find((el) => el.isDefault);
    return defaultOwner?.id!;  // Assuming there's always a defaultOwner
  };
  const prepareTime = async () => {
    try {
      const sourceOwnerId = await getDefaultSourceOwnerId();
      const data = await priceCollectedTime(sourceOwnerId);
      if (refIsMounted.current) {
        // TODO: we have to add useSafeFetch for making safe requests
        const dateFormat = UserContext.getDateFormat();
        const userTimeZone = UserContext.getTimeZone() || 'UTC';
        const readyTime = moment.tz(data ?? '', userTimeZone).format(dateFormat);
        if (readyTime) {
          setTime(readyTime);
        }
      }
    } catch (e) {
      message.error(e);
      console.error(e);
    }
  };

  const handleRun = async () => {
    setIsRun(true);
    await onConfirm();
    setIsRun(false);
    closeModalWithUpdate();
  };

  useEffect(() => {
    prepareTime();
    return () => {
      refIsMounted.current = false;
    };
  }, []);

  return (
    <ConfirmationModal
      visible={visible}
      confirmText="Run"
      className={styles.container}
      destroyOnClose={true}
      confirmDisabled={!time || isRun}
      onClose={onClose}
      onConfirm={handleRun}
    >
      <h1 className={styles.title}>
        Are you sure you want to run <span>{alertName}</span>?
      </h1>
      <div className={styles.xRow}>
        <h4 className={styles.text}>Results of this run will be based on data collected from {time}</h4>
        <Spin spinning={!time} className={styles.spinning} />
        <Popover placement="right" content={contentPopover}>
          <FontAwesomeIcon icon={['fal', 'info-circle']} className={styles.infoIcon} />
        </Popover>
      </div>
      <div className={styles.xRow}>
        <Spin spinning={isRun} className={styles.spinning} />
      </div>
    </ConfirmationModal>
  );
};
